
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Division } from '@/types/division';

export const useDivisionManagement = (seasonId?: string) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDivision, setEditingDivision] = useState<Division | null>(null);
  const [divisionName, setDivisionName] = useState('');
  const [travelLevel, setTravelLevel] = useState('none');

  const queryClient = useQueryClient();

  // Fetch divisions for a specific season using division_stats view
  const { data: divisions, isLoading } = useQuery({
    queryKey: ['divisions', seasonId],
    queryFn: async () => {
      if (!seasonId) return [];
      
      const { data, error } = await supabase
        .from('division_stats')
        .select('*')
        .eq('season_id', seasonId)
        .order('name');
      
      if (error) {
        console.error('Error fetching divisions:', error);
        throw error;
      }
      return data as Division[];
    },
    enabled: !!seasonId,
  });

  // Create division mutation
  const createDivisionMutation = useMutation({
    mutationFn: async ({ name, seasonId, travelLevel }: { 
      name: string; 
      seasonId: string; 
      travelLevel?: string;
    }) => {
      
      const divisionData: { 
        name: string; 
        season_id: string; 
        travel_level?: 'local' | 'in_state' | 'out_of_state' | 'international' | null;
      } = { 
        name, 
        season_id: seasonId 
      };
      
      if (travelLevel && travelLevel !== 'none') {
        divisionData.travel_level = travelLevel as 'local' | 'in_state' | 'out_of_state' | 'international';
      }
      
      const { error } = await supabase
        .from('divisions')
        .insert([divisionData]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['divisions', seasonId] });
      toast.success('Division created successfully');
      handleDialogClose();
    },
    onError: (error: Error) => {
      console.error('Create division error:', error);
      if (error.message.includes('unique_division_name_per_season')) {
        toast.error('A division with this name already exists in this season');
      } else {
        toast.error('Failed to create division');
      }
    },
  });

  // Update division mutation
  const updateDivisionMutation = useMutation({
    mutationFn: async ({ id, name, travelLevel }: { 
      id: string; 
      name: string;
      travelLevel?: string;
    }) => {
      
      const updateData: { 
        name: string; 
        updated_at: string; 
        travel_level?: 'local' | 'in_state' | 'out_of_state' | 'international' | null;
      } = { 
        name, 
        updated_at: new Date().toISOString() 
      };
      
      if (travelLevel && travelLevel !== 'none') {
        updateData.travel_level = travelLevel as 'local' | 'in_state' | 'out_of_state' | 'international';
      } else {
        updateData.travel_level = null;
      }
      
      const { error } = await supabase
        .from('divisions')
        .update(updateData)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['divisions', seasonId] });
      toast.success('Division updated successfully');
      handleDialogClose();
    },
    onError: (error: Error) => {
      console.error('Update division error:', error);
      if (error.message.includes('unique_division_name_per_season')) {
        toast.error('A division with this name already exists in this season');
      } else {
        toast.error('Failed to update division');
      }
    },
  });

  // Delete division mutation
  const deleteDivisionMutation = useMutation({
    mutationFn: async (divisionId: string) => {
      
      // First check if there are teams assigned to this division
      const { data: teams, error: teamsError } = await supabase
        .from('teams')
        .select('id, name')
        .eq('division_id', divisionId);
      
      if (teamsError) throw teamsError;
      
      if (teams && teams.length > 0) {
        throw new Error(`Cannot delete division with ${teams.length} team(s) assigned. Please reassign or remove teams first.`);
      }
      
      const { error } = await supabase
        .from('divisions')
        .delete()
        .eq('id', divisionId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['divisions', seasonId] });
      toast.success('Division deleted successfully');
    },
    onError: (error: Error) => {
      console.error('Delete division error:', error);
      toast.error(error.message || 'Failed to delete division');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!divisionName.trim() || !seasonId) return;

    if (editingDivision) {
      updateDivisionMutation.mutate({ 
        id: editingDivision.id, 
        name: divisionName.trim(),
        travelLevel
      });
    } else {
      createDivisionMutation.mutate({ 
        name: divisionName.trim(), 
        seasonId,
        travelLevel
      });
    }
  };

  const handleEdit = (division: Division) => {
    setEditingDivision(division);
    setDivisionName(division.name);
    setTravelLevel(division.travel_level || 'none');
    setIsDialogOpen(true);
  };

  const handleDelete = (divisionId: string) => {
    const division = divisions?.find(d => d.id === divisionId);
    const divisionName = division ? division.name : 'this division';
    
    if (window.confirm(`Are you sure you want to delete "${divisionName}"? This action cannot be undone.`)) {
      deleteDivisionMutation.mutate(divisionId);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingDivision(null);
    setDivisionName('');
    setTravelLevel('none');
  };

  return {
    divisions,
    isLoading,
    isDialogOpen,
    setIsDialogOpen,
    editingDivision,
    divisionName,
    setDivisionName,
    travelLevel,
    setTravelLevel,
    birthDateCutoff: '', // Keep for compatibility but not used
    setBirthDateCutoff: () => {}, // Keep for compatibility but not used
    handleSubmit,
    handleEdit,
    handleDelete,
    handleDialogClose,
    isCreating: createDivisionMutation.isPending,
    isUpdating: updateDivisionMutation.isPending,
    isDeleting: deleteDivisionMutation.isPending,
  };
};
